import { EventEmitter } from '../events/EventEmitter';
import { ConnectionManager } from './ConnectionManager';
import { CommandQueue } from './CommandQueue';
import type {
  ConnectionStatus,
  ConnectionConfig,
} from '../types/connection';
import type {
  CaptureMode,
  SDKCaptureResult,
  CaptureProgress,
  VideoFrame,
  BiometricPacket,
  StreamModality,
  CaptureStatus,
} from '../types/biometric';
import type { PowerState, PowerComponent } from '../types/device';
import type {
  BridgeClientEvents,
  CommandResponse,
  CaptureResultEvent,
  VideoFrameEvent,
} from '../types/events';
import type {
  BridgeClientOptions,
  QueueConfig,
  RetryConfig,
  LoggingConfig,
  Logger,
} from '../types/config';
import { DEFAULT_QUEUE_CONFIG, DEFAULT_RETRY_CONFIG, DEFAULT_LOGGING_CONFIG } from '../types/config';
import { DEFAULT_CONNECTION_CONFIG } from '../types/connection';
import {
  NotConnectedError,
  InvalidResponseError,
  CaptureCancelledError,
  toBridgeError,
} from '../errors/BridgeError';
import { BackoffRetryStrategy, type RetryStrategy, type RetryContext } from '../errors/RetryStrategy';

/**
 * Capture options
 */
export interface CaptureOptions {
  /** Timeout in milliseconds */
  timeoutMs?: number;

  /** Minimum quality score (0-100) */
  minQuality?: number;

  /** Enable video preview */
  preview?: boolean;

  /** Additional capture parameters */
  params?: Record<string, unknown>;
}

/**
 * Cancellable capture handle
 */
export interface CancellableCapture {
  /** Promise that resolves with capture result */
  promise: Promise<SDKCaptureResult>;

  /** Cancel the capture */
  cancel: () => void;

  /** Request ID */
  requestId: string;
}

/**
 * Bridge client configuration (resolved)
 */
interface ResolvedConfig {
  connection: ConnectionConfig;
  queue: QueueConfig;
  retry: RetryConfig;
  logging: LoggingConfig;
}

/**
 * Main SDK client for communicating with Iron Bridge
 */
export class BridgeClient extends EventEmitter<BridgeClientEvents> {
  private readonly connectionManager: ConnectionManager;
  private readonly commandQueue: CommandQueue;
  private readonly config: ResolvedConfig;
  private readonly logger: Logger;
  private readonly retryStrategy: RetryStrategy;

  private isStreaming = false;
  private currentCaptureId: string | null = null;

  constructor(options?: BridgeClientOptions) {
    super();

    // Resolve configuration
    this.config = this.resolveConfig(options);

    // Create logger
    this.logger = this.config.logging.logger ?? this.createDefaultLogger();

    // Create retry strategy
    this.retryStrategy = new BackoffRetryStrategy(this.config.retry);

    // Create connection manager
    this.connectionManager = new ConnectionManager({
      config: this.config.connection,
      webSocketFactory: this.config.connection.websocketFactory,
      logger: this.logger,
    });

    // Create command queue
    this.commandQueue = new CommandQueue(
      this.config.queue,
      this.config.connection.url.includes('localhost') ? 'local_' : 'req_'
    );

    // Setup event forwarding
    this.setupEventForwarding();
  }

  // ============================================================================
  // CONNECTION METHODS
  // ============================================================================

  /**
   * Connect to the Iron Bridge
   */
  async connect(): Promise<void> {
    this.logger.info('Connecting to Iron Bridge...');
    await this.connectionManager.connect();
    this.logger.info('Connected to Iron Bridge');
  }

  /**
   * Disconnect from the Iron Bridge
   */
  async disconnect(): Promise<void> {
    this.logger.info('Disconnecting from Iron Bridge...');

    // Stop any active stream
    if (this.isStreaming) {
      this.stopStream();
    }

    // Clear pending commands
    this.commandQueue.clearAll(new NotConnectedError());

    await this.connectionManager.disconnect();
    this.logger.info('Disconnected from Iron Bridge');
  }

  /**
   * Reconnect to the Iron Bridge
   */
  async reconnect(): Promise<void> {
    this.logger.info('Reconnecting to Iron Bridge...');
    await this.connectionManager.reconnect();
    this.logger.info('Reconnected to Iron Bridge');
  }

  /**
   * Get current connection status
   */
  get connectionStatus(): ConnectionStatus {
    return this.connectionManager.getStatus();
  }

  /**
   * Check if connected
   */
  get isConnected(): boolean {
    return this.connectionManager.isConnected();
  }

  // ============================================================================
  // CAPTURE METHODS
  // ============================================================================

  /**
   * Start a biometric capture (returns cancellable handle)
   *
   * @param mode - Capture mode (fingerprint, face, iris, card)
   * @param options - Capture options
   * @returns Cancellable capture handle
   */
  capture(mode: CaptureMode, options?: CaptureOptions): CancellableCapture {
    let cancelled = false;
    let requestId = '';

    const promise = new Promise<SDKCaptureResult>((resolve, reject) => {
      if (!this.isConnected) {
        reject(new NotConnectedError());
        return;
      }

      const params: Record<string, unknown> = {
        modality: mode,  // Iron Bridge protocol uses 'modality' field
        ...options?.params,
      };

      // Convert timeout from milliseconds to seconds for Iron Bridge protocol
      if (options?.timeoutMs !== undefined) {
        params.timeout = Math.ceil(options.timeoutMs / 1000);
      }

      if (options?.minQuality !== undefined) {
        params.min_quality = options.minQuality;
      }

      if (options?.preview !== undefined) {
        params.preview = options.preview;
      }

      const { id, promise: cmdPromise } = this.commandQueue.enqueue<SDKCaptureResult>(
        'capture',
        params,
        options?.timeoutMs
      );

      requestId = id;
      this.currentCaptureId = id;

      // Send capture command
      this.sendRawCommand('capture', params, id);

      cmdPromise
        .then((result) => {
          if (cancelled) {
            reject(new CaptureCancelledError());
          } else {
            this.currentCaptureId = null;
            resolve(result);
          }
        })
        .catch((error) => {
          this.currentCaptureId = null;
          reject(error);
        });
    });

    return {
      promise,
      requestId,
      cancel: () => {
        cancelled = true;
        if (requestId && this.currentCaptureId === requestId) {
          this.sendCommand('cancel_capture', { request_id: requestId });
          this.currentCaptureId = null;
        }
      },
    };
  }

  /**
   * Perform a biometric capture (async/await style)
   *
   * @param mode - Capture mode
   * @param options - Capture options
   * @returns Promise resolving to capture result
   */
  async captureAsync(mode: CaptureMode, options?: CaptureOptions): Promise<SDKCaptureResult> {
    return this.capture(mode, options).promise;
  }

  // ============================================================================
  // VIDEO STREAMING
  // ============================================================================

  /**
   * Start video preview stream
   *
   * @param modality - Stream modality (face, iris)
   */
  startStream(modality: StreamModality, options?: { autoCapture?: boolean }): void {
    if (!this.isConnected) {
      throw new NotConnectedError();
    }

    // auto_capture: true (default) = frontal with face_crop/liveness, false = side faces raw only
    const autoCapture = options?.autoCapture ?? true;
    this.sendCommand('start_stream', { modality, auto_capture: autoCapture });
    this.isStreaming = true;
    this.emit('video:started');
  }

  /**
   * Stop video preview stream
   */
  stopStream(): void {
    if (this.isStreaming) {
      this.sendCommand('stop_stream');
      this.isStreaming = false;
      this.emit('video:stopped');
    }
  }

  /**
   * Check if video is streaming
   */
  get streaming(): boolean {
    return this.isStreaming;
  }

  // ============================================================================
  // COMMAND METHODS
  // ============================================================================

  /**
   * Send a command (fire and forget)
   *
   * @param action - Command action
   * @param params - Command parameters
   */
  sendCommand(action: string, params?: Record<string, unknown>): void {
    if (!this.isConnected) {
      throw new NotConnectedError();
    }

    const id = this.commandQueue.generateRequestId();
    this.sendRawCommand(action, params, id);
  }

  /**
   * Send a command and wait for response
   *
   * @param action - Command action
   * @param params - Command parameters
   * @param timeoutMs - Optional timeout
   * @returns Promise resolving to command response
   */
  async sendCommandAsync<T = unknown>(
    action: string,
    params?: Record<string, unknown>,
    timeoutMs?: number
  ): Promise<T> {
    if (!this.isConnected) {
      throw new NotConnectedError();
    }

    const { id, promise } = this.commandQueue.enqueue<T>(action, params, timeoutMs);
    this.sendRawCommand(action, params, id);

    return promise;
  }

  // ============================================================================
  // POWER MANAGEMENT
  // ============================================================================

  /**
   * Power on a component (fire-and-forget, Iron Bridge protocol)
   *
   * @param component - Optional component to power on (defaults to 'all')
   */
  powerOn(component: PowerComponent = 'all'): void {
    // Iron Bridge protocol: { type: 'power', component, action: 'on' }
    this.sendCommand('power', { component, action: 'on' });
  }

  /**
   * Power off a component (fire-and-forget, Iron Bridge protocol)
   *
   * @param component - Optional component to power off (defaults to 'all')
   */
  powerOff(component: PowerComponent = 'all'): void {
    // Iron Bridge protocol: { type: 'power', component, action: 'off' }
    this.sendCommand('power', { component, action: 'off' });
  }

  // ============================================================================
  // DEVICE METHODS
  // ============================================================================

  /**
   * Get list of connected devices
   */
  async getDevices(): Promise<unknown[]> {
    const status = await this.getStatus() as { devices?: unknown[] };
    return status?.devices ?? [];
  }

  /**
   * Get bridge status
   */
  async getStatus(): Promise<unknown> {
    return this.sendCommandAsync('get_status');
  }

  // ============================================================================
  // VERIFICATION METHODS
  // ============================================================================

  /**
   * Verify (1:1 match) two fingerprint templates
   *
   * Performs on-device template matching using Aratek's built-in algorithm.
   * Returns a match score (0-100) and whether templates match based on threshold.
   *
   * @param template1 - First template (probe) - base64-encoded ISO template
   * @param template2 - Second template (gallery) - base64-encoded ISO template
   * @param options - Optional verification options
   * @returns Promise with verification result
   *
   * @example
   * ```typescript
   * const result = await client.verifyFingerprint(
   *   capturedTemplate,
   *   enrolledTemplate,
   *   { threshold: 40 }
   * );
   * if (result.matched) {
   *   console.log(`Match confirmed with score ${result.score}`);
   * }
   * ```
   */
  async verifyFingerprint(
    template1: string,
    template2: string,
    options?: {
      /** Match threshold (0-100). Default: 40 (Aratek recommendation) */
      threshold?: number;
      /** Timeout in milliseconds. Default: 10000 */
      timeoutMs?: number;
    }
  ): Promise<{
    success: boolean;
    score?: number;
    matched?: boolean;
    threshold?: number;
    error?: string;
  }> {
    if (!this.isConnected) {
      throw new NotConnectedError();
    }

    const threshold = options?.threshold ?? 40;
    const timeoutMs = options?.timeoutMs ?? 10000;

    return this.sendCommandAsync('verify', {
      template1,
      template2,
      modality: 'fingerprint',
      threshold,
    }, timeoutMs);
  }

  /**
   * Generalize (merge) multiple fingerprint templates into enrollment template
   *
   * Creates a higher-quality template by combining multiple captures of the same finger.
   * Aratek recommends 3 captures for best results.
   *
   * @param templates - Array of base64-encoded ISO templates to merge
   * @param options - Optional generalization options
   * @returns Promise with generalization result
   *
   * @example
   * ```typescript
   * const result = await client.generalizeFingerprint(
   *   [template1, template2, template3],
   *   { fingerPos: 2 }
   * );
   * if (result.success) {
   *   console.log(`Enrollment template quality: ${result.quality}`);
   *   saveToDatabase(result.template);
   * }
   * ```
   */
  async generalizeFingerprint(
    templates: string[],
    options?: {
      /** Finger position (1-10 ISO). Optional but recommended. */
      fingerPos?: number;
      /** Timeout in milliseconds. Default: 15000 */
      timeoutMs?: number;
    }
  ): Promise<{
    success: boolean;
    template?: string;
    quality?: number;
    finger_pos?: number;
    template_count?: number;
    error?: string;
  }> {
    if (!this.isConnected) {
      throw new NotConnectedError();
    }

    if (!templates || templates.length === 0) {
      throw new Error('At least one template is required for generalization');
    }

    const timeoutMs = options?.timeoutMs ?? 15000;

    return this.sendCommandAsync('generalize', {
      templates,
      modality: 'fingerprint',
      finger_pos: options?.fingerPos,
    }, timeoutMs);
  }

  // ============================================================================
  // RETRY WITH STRATEGY
  // ============================================================================

  /**
   * Execute an operation with retry strategy
   *
   * @param operation - Operation to execute
   * @returns Promise resolving to operation result
   */
  async withRetry<T>(operation: () => Promise<T>): Promise<T> {
    const startTime = new Date();
    let attempt = 0;
    let lastError: Error | null = null;

    while (true) {
      attempt++;

      try {
        return await operation();
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));
        const bridgeError = toBridgeError(lastError);

        const context: RetryContext = {
          attempt,
          error: bridgeError,
          startTime,
          lastAttemptTime: new Date(),
          elapsedMs: Date.now() - startTime.getTime(),
        };

        const decision = this.retryStrategy.shouldRetry(context);

        if (!decision.shouldRetry) {
          this.logger.warn(`Retry stopped: ${decision.reason}`);
          throw lastError;
        }

        this.logger.info(`Retrying: ${decision.reason}`);
        await this.delay(decision.delayMs);
      }
    }
  }

  // ============================================================================
  // PRIVATE METHODS
  // ============================================================================

  /**
   * Resolve configuration with defaults
   */
  private resolveConfig(options?: BridgeClientOptions): ResolvedConfig {
    return {
      connection: { ...DEFAULT_CONNECTION_CONFIG, ...options?.connection },
      queue: { ...DEFAULT_QUEUE_CONFIG, ...options?.queue },
      retry: { ...DEFAULT_RETRY_CONFIG, ...options?.retry },
      logging: { ...DEFAULT_LOGGING_CONFIG, ...options?.logging },
    };
  }

  /**
   * Create default logger
   */
  private createDefaultLogger(): Logger {
    const level = this.config.logging.level;
    const levels = ['debug', 'info', 'warn', 'error', 'silent'];
    const levelIndex = levels.indexOf(level);

    const shouldLog = (msgLevel: string): boolean => {
      if (level === 'silent') return false;
      return levels.indexOf(msgLevel) >= levelIndex;
    };

    const format = (msgLevel: string, message: string): string => {
      const timestamp = this.config.logging.timestamps
        ? `[${new Date().toISOString()}] `
        : '';
      return `${timestamp}[${msgLevel.toUpperCase()}] ${message}`;
    };

    return {
      debug: (message: string, ...args: unknown[]) => {
        if (shouldLog('debug')) console.debug(format('debug', message), ...args);
      },
      info: (message: string, ...args: unknown[]) => {
        if (shouldLog('info')) console.info(format('info', message), ...args);
      },
      warn: (message: string, ...args: unknown[]) => {
        if (shouldLog('warn')) console.warn(format('warn', message), ...args);
      },
      error: (message: string, ...args: unknown[]) => {
        if (shouldLog('error')) console.error(format('error', message), ...args);
      },
    };
  }

  /**
   * Setup event forwarding from internal components
   */
  private setupEventForwarding(): void {
    // Forward connection events
    this.connectionManager.on('status', (status) => {
      this.emit('connection:status', status);
    });

    this.connectionManager.on('error', (error) => {
      this.emit('connection:error', error);
    });

    // Handle incoming messages
    this.connectionManager.on('message', (data) => {
      this.handleMessage(data);
    });
  }

  /**
   * Handle incoming WebSocket message
   */
  private handleMessage(data: string): void {
    // Emit raw message for debugging
    if (this.config.logging.logMessages) {
      this.emit('message:raw', data);
    }

    try {
      const message = JSON.parse(data);
      this.routeMessage(message);
    } catch (error) {
      this.logger.error('Failed to parse message:', error);
      this.emit('error', toBridgeError(new InvalidResponseError('Invalid JSON', data)));
    }
  }

  /**
   * Route message to appropriate handler
   */
  private routeMessage(message: Record<string, unknown>): void {
    const type = message.type as string;

    // Emit wildcard event
    this.emit('*', type, message);

    switch (type) {
      case 'capture_result':
        this.handleSDKCaptureResult(message as unknown as CaptureResultEvent);
        break;

      case 'video_frame':
        this.handleVideoFrame(message as unknown as VideoFrameEvent);
        break;

      case 'capture_progress':
        this.handleCaptureProgress(message as unknown as CaptureProgress);
        break;

      case 'capture_status':
        this.handleCaptureStatus(message as { status: CaptureStatus });
        break;

      case 'power_state_changed':
        this.handlePowerStateChanged(message as { state: PowerState });
        break;

      case 'device_discovered':
        this.emit('device:discovered', message as never);
        break;

      case 'device_disconnected':
        this.emit('device:disconnected', message.deviceId as string);
        break;

      case 'stream_started':
        // Iron Bridge confirms stream has started
        this.isStreaming = true;
        this.emit('video:started');
        break;

      case 'stream_stopped':
        // Iron Bridge confirms stream has stopped
        this.isStreaming = false;
        this.emit('video:stopped');
        break;

      case 'connected':
        // Iron Bridge connection established - log version info
        this.logger.info('Bridge connected:', message.version, 'drivers:', message.drivers);
        break;

      case 'status':
        // Handle status response - resolve any pending get_status command
        if (message.request_id) {
          this.handleCommandResponse(message as unknown as CommandResponse);
        }
        break;

      case 'response':
      case 'command_response':
        this.handleCommandResponse(message as unknown as CommandResponse);
        break;

      case 'error':
        this.handleErrorMessage(message);
        break;

      default:
        // Try to resolve as command response by request_id
        if (message.request_id) {
          this.handleCommandResponse(message as unknown as CommandResponse);
        }
    }
  }

  /**
   * Handle capture result
   */
  private handleSDKCaptureResult(event: CaptureResultEvent): void {
    // Use event.request_id if present, otherwise fallback to currentCaptureId
    // Iron Bridge doesn't echo request_id, so we track it ourselves
    const requestId = event.request_id || this.currentCaptureId;
    
    if (requestId && this.commandQueue.isPending(requestId)) {
      if (event.success && event.data) {
        try {
          // Decode base64 BiometricPacket
          const decoded = this.decodeBase64(event.data);
          const packet: BiometricPacket = JSON.parse(decoded);
          const result: SDKCaptureResult = {
            success: true,
            data: packet,
            quality: event.quality,
            format: event.format,
          };
          this.commandQueue.resolve(requestId, result);
          this.currentCaptureId = null;
          this.emit('capture:result', result);
        } catch (error) {
          this.logger.error('Failed to decode capture result:', error);
          this.commandQueue.reject(
            requestId,
            new InvalidResponseError('Invalid capture data')
          );
          this.currentCaptureId = null;
        }
      } else if (event.payload) {
        // Legacy direct payload
        this.commandQueue.resolve(requestId, event.payload);
        this.currentCaptureId = null;
        this.emit('capture:result', event.payload);
      } else {
        this.commandQueue.reject(
          requestId,
          new InvalidResponseError(event.error ?? 'Capture failed')
        );
        this.currentCaptureId = null;
      }
    } else if (event.success && event.data) {
      // No pending command but we still emit the result for event-based listeners
      try {
        const decoded = this.decodeBase64(event.data);
        const packet: BiometricPacket = JSON.parse(decoded);
        const result: SDKCaptureResult = {
          success: true,
          data: packet,
          quality: event.quality,
          format: event.format,
        };
        this.emit('capture:result', result);
      } catch (error) {
        this.logger.error('Failed to decode capture result (no pending command):', error);
      }
    }
  }

  /**
   * Handle video frame
   */
  private handleVideoFrame(event: VideoFrameEvent): void {
    const frame: VideoFrame = {
      data: event.data,
      format: event.format,
      width: event.width,
      height: event.height,
      timestamp: Date.now(),
      // Pass through DMA face detection data for instant capture
      face_crop: event.face_crop,
      face_detect: event.face_detect,
      live_detect: event.live_detect,
    };
    this.emit('video:frame', frame);
  }

  /**
   * Handle capture progress
   */
  private handleCaptureProgress(progress: CaptureProgress): void {
    this.emit('capture:progress', progress);
  }

  /**
   * Handle capture status change
   */
  private handleCaptureStatus(message: { status: CaptureStatus }): void {
    this.emit('capture:status', message.status);
  }

  /**
   * Handle power state change
   */
  private handlePowerStateChanged(message: { state: PowerState }): void {
    this.emit('power:changed', message.state);
  }

  /**
   * Handle command response
   */
  private handleCommandResponse(response: CommandResponse): void {
    if (!response.request_id) return;

    if (response.success) {
      this.commandQueue.resolve(response.request_id, response.data);
    } else {
      this.commandQueue.reject(
        response.request_id,
        new InvalidResponseError(response.error ?? 'Command failed')
      );
    }
  }

  /**
   * Handle error message
   */
  private handleErrorMessage(message: Record<string, unknown>): void {
    const error = toBridgeError(
      new InvalidResponseError(message.message as string ?? 'Unknown error')
    );

    // Reject pending command if request_id present
    if (message.request_id) {
      this.commandQueue.reject(message.request_id as string, error);
    }

    this.emit('error', error);
  }

  /**
   * Send raw command to WebSocket
   */
  private sendRawCommand(
    action: string,
    params?: Record<string, unknown>,
    requestId?: string
  ): void {
    // Iron Bridge protocol uses "type" field for command type
    const command = {
      type: action,
      request_id: requestId,
      ...params,
    };

    this.connectionManager.send(JSON.stringify(command));
  }

  /**
   * Decode base64 string
   */
  private decodeBase64(data: string): string {
    if (typeof atob === 'function') {
      return atob(data);
    }
    // Node.js
    return Buffer.from(data, 'base64').toString('utf-8');
  }

  /**
   * Delay for specified milliseconds
   */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}

/**
 * Create a new BridgeClient instance
 */
export function createBridgeClient(options?: BridgeClientOptions): BridgeClient {
  return new BridgeClient(options);
}
